import {
  Component, createNgModule,
  CUSTOM_ELEMENTS_SCHEMA, forwardRef, inject,
  Injectable, Injector,
  NgModule,
  NO_ERRORS_SCHEMA,
  ViewEncapsulation
} from "@angular/core";

import {
  Parent,
  WidgetRef
} from '@wm/components-base';
import { getValidJSON, runtimeEventManager } from '@wm/core';
import { transpile, scopeComponentStyles } from '@wm/transpiler';
import {
  NativeMobileModule,
  RuntimeBaseModule,
} from "@wm/design-runtime";

import { BaseVariableService } from "@wms/variables";
import { ProjectService, BindVariableService } from "@wms/services";
import { EventManagerService } from "@wms/app";
import { MarkupManagerService } from "@wms/core";

import { BasePageComponent } from '../components/base-page.component';
import { BasePartialComponent } from '../components/base-partial.component';
import { BasePrefabComponent } from '../components/base-prefab.component';
import { AppResourceManagerService } from './app-resource-manager.service';
import { AppPrefabResourceManagerService } from './app-prefab-resource-manager.service';
import { ComponentRefProvider, ComponentType } from '../types/types';
import {
  getPrefabMinJsonUrl,
  getPrefabPartialJsonUrl,
  PIPES,
  RN_SPECIFIC_STANDALONE_COMPONENTS,
  STANDALONE_COMPONENTS
} from '../util/utils';
import {get, isString, isUndefined, set} from "lodash-es";
import { AppManagerService } from './app.manager.service';
import { AppRef } from "./app.service";
import { environment } from "@wms/app";
import { PageWsService } from "@wms/workspace";
import { BaseCustomWidgetComponent } from "../components/base-custom-widget.component ";
import * as customWidgets from '@wavemaker/custom-widgets-m3';

export interface IPageMinJSON {
  markup: string;
  script: string;
  styles: string;
  variables: string;
  config?: string;
}

const fragmentCache = new Map<string, any>();

set(window, 'resourceCache', fragmentCache);

const componentFactoryRefCache = new Map<ComponentType, Map<string, any>>();

componentFactoryRefCache.set(ComponentType.PAGE, new Map<string, any>());
componentFactoryRefCache.set(ComponentType.PARTIAL, new Map<string, any>());
componentFactoryRefCache.set(ComponentType.PREFAB, new Map<string, any>());
componentFactoryRefCache.set(ComponentType.WIDGET, new Map<string, any>());


export const _decodeURIComponent = (str: string) => decodeURIComponent(str.replace(/\+/g, ' '));

// @ts-ignore
export const getFragmentUrl = (fragmentName: string, type: ComponentType, options?) => {
  if (type === ComponentType.PAGE || type === ComponentType.PARTIAL) {
    return options && options.prefab ?
      getPrefabPartialJsonUrl(options.prefab, options.projectId, fragmentName) :
      environment.SERVICES_BASE_PATH + `services/projects/${options.projectId}/pages/${fragmentName}/page.min.json`;
  } else if (type === ComponentType.PREFAB) {
    return getPrefabMinJsonUrl(fragmentName, options.projectId);
  } else if (type === ComponentType.WIDGET) {
    return environment.SERVICES_BASE_PATH + `services/projects/${options.projectId}/resources/content/project/src/main/webapp/custom-widgets/${fragmentName}/page.min.json`;
  }
};

const scriptCache = new Map<string, Function>();
const execScript = (
  script: string,
  identifier: string,
  ctx: string,
  instance: any,
  app: any,
  utils: any
) => {
  let fn = scriptCache.get(identifier);
  // Fix for [WMS-21196]: Incorrect script is being assigned,  when 2 prefabs have same partial name (i.e. same identifier).
  if ((ctx === 'Partial' && !isUndefined(instance.Prefab)) || !fn) {
    fn = new Function(ctx, 'App', 'Utils', script);
    scriptCache.set(identifier, fn);
  }
  fn(instance, app, utils);
};

class BaseDynamicComponent {
  constructor(public inj: Injector) {
  }
  init() { }
}

const getDynamicModule = (componentRef: any, widgetRef: any, isNativeApp: boolean) => {
  let imports = [
    RuntimeBaseModule,
    ...STANDALONE_COMPONENTS,
    ...PIPES,
    ...(isNativeApp ? RN_SPECIFIC_STANDALONE_COMPONENTS : [])
  ];

  return NgModule({
    declarations: [
      componentRef
    ],
    imports: imports,
    providers: [
      { provide: Parent, useExisting: forwardRef(() => componentRef) },
      { provide: WidgetRef, useValue: widgetRef || null },
      ...PIPES
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
  })(
    class DynamicModule { }
  )
};

const getDynamicComponent = (
  componentName: string,
  type: ComponentType,
  template: string,
  css: string,
  script: any,
  variables: string
) => {
  let BaseClass: any = BaseDynamicComponent;
  let selector = '';
  let context = '';

  switch (type) {
    case ComponentType.PAGE:
      BaseClass = BasePageComponent;
      selector = `app-page-${componentName}`;
      context = 'Page';
      break;
    case ComponentType.PARTIAL:
      BaseClass = BasePartialComponent;
      selector = `app-partial-${componentName}`;
      context = 'Partial';
      break;
    case ComponentType.PREFAB:
      BaseClass = BasePrefabComponent;
      selector = `app-prefab-${componentName}`;
      context = 'Prefab';
      break;
    case ComponentType.WIDGET:
      BaseClass = BaseCustomWidgetComponent;
      selector = `app-custom-${componentName}`;
      context = 'Widget';
      break;
  }

  // Define the component using Component decorator.
  // @ts-ignore
  const component = Component({
    selector: selector,
    template: template,
    encapsulation: ViewEncapsulation.None,
    styles: [css],
    providers: [
      { provide: Parent, useExisting: forwardRef(() => component) }
    ]
  })(
    class DynamicComponent extends BaseClass {
      pageName;
      partialName;
      prefabName;
      customWidgetName;

      constructor() {
        super();

        switch (type) {
          case ComponentType.PAGE:
            this.pageName = componentName;
            break;
          case ComponentType.PARTIAL:
            this.partialName = componentName;
            break;
          case ComponentType.PREFAB:
            this.prefabName = componentName;
            break;
          case ComponentType.WIDGET:
            this.customWidgetName = componentName;
            break;
        }

        super.init();
      }

      evalUserScript(instance: any, appContext: any, utils: any) {
        execScript(script, selector, context, instance, appContext, utils);
      }

      getVariables() {
        return JSON.parse(variables);
      }

      // in preview mode, there will be no function registered. functions will be generated dynamically through $parseEvent and $parseExpr
      getExpressions() {
        return {}
      }
    }
  )
  return component;
}
export interface IHaveDynamicData {
  // entity: any;
}

export class ITestClass {

}

@Injectable()
export class ComponentRefProviderService extends ComponentRefProvider {
  public prepareElements: any;

  public updateFragmentResources(content: IPageMinJSON, componentName: string, componentType: ComponentType, options?: any) {
    options = options || {};
    options.projectId = this.projectService.getProjectId();

    const url: string = getFragmentUrl(componentName, componentType, options) || '';

    if (this.updateRequiredForPage !== componentName) {
      this.updateRequiredForPage = '';
      let pageResponse = fragmentCache.get(url);
      if (pageResponse) {
        return pageResponse;
      }
    }
    let response = this.processPageResponse({ markup: content.markup, script: content.script, styles: content.styles, variables: content.variables },
      componentName, componentType, url);
    fragmentCache.set(url, Promise.resolve(response));
  }

  private processPageResponse({ markup, script, styles, variables, config }: IPageMinJSON, componentName: string, componentType: ComponentType, url: string, listener?: Function) {
    let decodedMarkup = _decodeURIComponent(markup);
    const variableInfo = getValidJSON(_decodeURIComponent(variables));
    this.variableService.setPageVariables(componentName, variableInfo, componentType);

    // Markup processing can be done through listener added in options. This is handled for prefab showAt widget scenario.
    if (listener) {
      decodedMarkup = listener(decodedMarkup)
    }

    let modifiedMarkup;
    if (this.prepareElements && componentType === ComponentType.PAGE) {
      if (componentType === ComponentType.PAGE) {
        this.markupManager.setMarkupRoot(decodedMarkup);
      }
      let _decodedMarkup: JQuery<HTMLElement> = this.prepareElements(decodedMarkup, true);
      modifiedMarkup = (_decodedMarkup[0] as any).innerHTML;
    } else {
      // this will not generate widgetId, names using prepareElements during rendering partial inside page
      modifiedMarkup = decodedMarkup;
    }
    const response = {
      markup: transpile(modifiedMarkup)?.markup as string || '',
      script: _decodeURIComponent(script),
      styles: scopeComponentStyles(componentName, componentType, _decodeURIComponent(styles)),
      variables: variableInfo,
      ...(config? {config : getValidJSON(_decodeURIComponent(config))} : {})
    };
    // markup is used in studio, to maintain same widgetid
    if (componentType === ComponentType.PAGE) {
      this.appManager.notify('canvas-markup', modifiedMarkup);
      this.pageWsService.splitMinFileContent(componentName, {...response, markup: modifiedMarkup}, true);
    }
    let pageData = {
      markup: markup,
      script: script,
      styles: styles
    }
    fragmentCache.set(url + ':page-data', pageData);
    fragmentCache.set(url, Promise.resolve(response));
    // this.appManager.notify('page-data', pageData);
    return response;
  }

  private loadResourcesOfFragment(componentName: string, componentType: any, options?: any): Promise<IPageMinJSON> {
    const projectId = this.projectService.getProjectId();
    let $promise: Promise<any> = Promise.resolve();
    const url: string = getFragmentUrl(componentName, componentType, options) || '';

    if (componentType === ComponentType.PAGE || componentType === ComponentType.PARTIAL ) {
      if (options && options.prefab) {
        $promise = this.prefabResourceMngr.getPageDataForPrefab(options.prefab, componentName);
      } else {
        $promise = this.resourceMngr.get(url, true)
      }
    } else if (componentType === ComponentType.PREFAB) {
      $promise = this.prefabResourceMngr.getPageDataForPrefab(componentName);
    } else if(componentType === ComponentType.WIDGET){
      if((customWidgets as any)[componentName]){
        $promise = Promise.resolve((customWidgets as any)[componentName])
      }else{
        $promise = this.resourceMngr.get(url, true)
      }
    }

    return $promise
      .then(({ markup, script, styles, variables, config }: IPageMinJSON) => {
        return this.processPageResponse({ markup, script, styles, variables, config }, componentName, componentType, url, options?.listener);
      }, (e: any) => {
        if(componentType === ComponentType.WIDGET) {
          return Promise.reject(e)
        }
        const status = e.details.status;
        const errorMsgMap: { [key: number]: string } = {
          404: this.app.appLocale.MESSAGE_PAGE_NOT_FOUND || 'The page you are trying to reach is not available',
          403: this.app.appLocale.LABEL_ACCESS_DENIED || 'Access Denied'
        };
        return Promise.reject(errorMsgMap[status]);
      });
  }
  private appManager: AppManagerService;
  private injector: Injector;
  private projectService: ProjectService;
  private eventManager: EventManagerService;
  private updateRequiredForPage: string;
  private isNativeApp: boolean;

  constructor(private variableService: BaseVariableService, private resourceMngr: AppResourceManagerService, private pageWsService: PageWsService,
    private prefabResourceMngr: AppPrefabResourceManagerService, private app: AppRef, private markupManager: MarkupManagerService) {
    super();
    this.appManager = inject(AppManagerService);
    this.injector = inject(Injector);
    this.projectService = inject(ProjectService);
    this.eventManager = inject(EventManagerService);

    runtimeEventManager.setService('componentRefProvider', this);

    this.eventManager.canvasMarkupDirtyObservable().subscribe((pageName: string) => {
      this.updateRequiredForPage = pageName;
    })

    this.isNativeApp = this.projectService.isNativeMobileApplicationType();
  }

  public getComponentFactoryRef(componentName: string, componentType: ComponentType, options?: any): Promise<any> {
    // check in the cache.
    const componentFactoryMap = componentFactoryRefCache.get(componentType);
    let componentFactoryRef;
    options = options || {};
    options.projectId = this.projectService.getProjectId();
    // TODO: revisit for caching
    // if (componentFactoryMap && !this.pageWsService.markupUpdated) {
    //   const updatedComponentName = (get(options, 'prefab') || '') +  componentName;
    //   componentFactoryRef = componentFactoryMap.get(updatedComponentName);
    //
    //   if (componentFactoryRef) {
    // //     const url: string = getFragmentUrl(componentName, componentType, options) || '';
    // //     const pageData = url && fragmentCache.get(url + ':page-data');
    // //     this.appManager.notify('page-data', pageData);
    //     return Promise.resolve(componentFactoryRef);
    //   }
    // }
    // this.pageWsService.markupUpdated = false;
    return this.loadResourcesOfFragment(componentName, componentType, options)
      .then(({ markup, script, styles, variables }) => {

        const componentDef: any = getDynamicComponent(componentName, componentType, markup, styles, script, JSON.stringify(variables));
        const module = getDynamicModule(componentDef, options.widgetRef, this.isNativeApp);

        const componentFactoryRef = this.app.dynamicComponentContainerRef.createComponent(componentDef, {
          injector: this.injector,
          ngModuleRef: createNgModule(module, this.injector),
        });

        this.markupManager.processMarkupForDialogs(markup, componentName, componentType);


        const updatedComponentName = (get(options, 'prefab') || '') + componentName;
        // @ts-ignore
        componentFactoryRefCache.get(componentType).set(updatedComponentName, componentFactoryRef);
        // TODO: remove this. temp adding for fixing : BrightBank Prefab changes where not reflecting
        setTimeout(() => {
          componentFactoryRef.changeDetectorRef.detectChanges();
        })
        return componentFactoryRef;
      }, (err) => {
        if (err) {
          const msg = "Error while loading page";
          // console the error for easy debugging
          console.log(msg, err);
          this.appManager.notifyApp(
            isString(err) ? err : msg + ', check browser console for error details',
            'error'
          );
        }
      });
  }

  // clears the cache map
  public override clearComponentFactoryRefCache() {
    this.resourceMngr.clearCache();
    fragmentCache.clear();
    componentFactoryRefCache.forEach(map => {
      map.clear();
    });
  }
}
